import { useEffect, useState } from "react";
import { api } from "@/client/lib/api";
import { AdminShell } from "@/client/components/admin/AdminShell";
import { AdminPageHeader } from "@/client/components/admin/AdminPageHeader";
import { Button } from "@/client/components/ui/button";
import { Skeleton } from "@/client/components/ui/skeleton";
import { DataTable, type DataTableColumn } from "@/client/components/admin/DataTable";
import { AlertCircleIcon, InfoIcon } from "lucide-react";

type Backup = {
  name: string;
  location: "daily" | "handmatig";
  path: string;
  sizeBytes: number;
  createdAt: string;
};

// Human-readable file size. pg_dump archives are well above 1 KB, so KB/MB cover every
// realistic case; we keep one decimal for MB so the size is meaningful at a glance.
function formatSize(bytes: number): string {
  if (!Number.isFinite(bytes) || bytes < 0) return "onbekend";
  if (bytes < 1024) return `${bytes} B`;
  const kb = bytes / 1024;
  if (kb < 1024) return `${Math.round(kb)} KB`;
  const mb = kb / 1024;
  return `${mb.toFixed(1)} MB`;
}

// Dutch long date + time, e.g. "11 juni 2026 om 23:59" in Europe/Amsterdam.
function formatDutchDateTime(iso: string): string {
  try {
    const d = new Date(iso);
    if (Number.isNaN(d.getTime())) return iso;
    const date = d.toLocaleDateString("nl-NL", {
      day: "numeric",
      month: "long",
      year: "numeric",
      timeZone: "Europe/Amsterdam",
    });
    const time = d.toLocaleTimeString("nl-NL", {
      hour: "2-digit",
      minute: "2-digit",
      timeZone: "Europe/Amsterdam",
    });
    return `${date} om ${time}`;
  } catch {
    return iso;
  }
}

function LocationBadge({ location }: { location: Backup["location"] }) {
  const isDaily = location === "daily";
  return (
    <span
      className={`inline-flex shrink-0 items-center rounded-full px-2 py-0.5 text-xs font-medium ${
        isDaily ? "bg-emerald-50 text-emerald-700" : "bg-blue-50 text-blue-700"
      }`}
    >
      {isDaily ? "Dagelijks" : "Handmatig"}
    </span>
  );
}

const COLUMNS: DataTableColumn<Backup>[] = [
  {
    key: "name",
    header: "Bestand",
    sortable: true,
    sortValue: (b) => b.name,
    headClassName: "w-full",
    cellClassName: "w-full",
    cell: (b) => (
      <div className="min-w-0 leading-tight">
        <div className="truncate font-mono text-[13px] font-medium text-foreground" title={b.name}>
          {b.name}
        </div>
        <div className="truncate font-mono text-[11px] text-muted-foreground/80" title={b.path}>
          {b.path}
        </div>
      </div>
    ),
  },
  {
    key: "location",
    header: "Type",
    sortable: true,
    sortValue: (b) => b.location,
    cell: (b) => <LocationBadge location={b.location} />,
  },
  {
    key: "createdAt",
    header: "Aangemaakt",
    sortable: true,
    sortValue: (b) => b.createdAt,
    cellClassName: "whitespace-nowrap tabular-nums text-muted-foreground",
    cell: (b) => formatDutchDateTime(b.createdAt),
  },
  {
    key: "sizeBytes",
    header: "Grootte",
    align: "right",
    sortable: true,
    sortValue: (b) => b.sizeBytes,
    cellClassName: "whitespace-nowrap font-mono tabular-nums text-muted-foreground",
    cell: (b) => formatSize(b.sizeBytes),
  },
];

export function BackupsPage() {
  const [backups, setBackups] = useState<Backup[]>([]);
  const [available, setAvailable] = useState(true);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);

  async function load() {
    setError(false);
    try {
      const res = await (api.api.admin as any).backups.$get();
      if (res.ok) {
        const data = await res.json();
        setBackups(Array.isArray(data.backups) ? data.backups : []);
        setAvailable(data.available !== false);
      } else {
        setError(true);
      }
    } catch {
      setError(true);
    }
    setLoading(false);
  }

  useEffect(() => {
    load();
  }, []);

  return (
    <AdminShell>
      <AdminPageHeader
        items={[{ label: "Back-ups" }]}
        subtitle="Dagelijkse automatische back-up om 23:59 (Europe/Amsterdam) plus handmatige back-ups."
      />

      {loading ? (
        <div className="space-y-3 rounded-lg border p-4">
          {[0, 1, 2, 3].map((i) => (
            <div key={i} className="space-y-2">
              <Skeleton className="h-4 w-2/3" />
              <Skeleton className="h-3 w-1/3" />
            </div>
          ))}
        </div>
      ) : error ? (
        <div className="rounded-lg border border-destructive/40 bg-destructive/5 p-6 text-sm">
          <p className="flex items-center gap-2 font-medium text-destructive">
            <AlertCircleIcon size={16} /> Kan de back-ups niet laden
          </p>
          <p className="mt-1 text-muted-foreground">Probeer het over enkele ogenblikken opnieuw.</p>
          <Button
            size="sm"
            variant="outline"
            className="mt-3"
            onClick={() => {
              setLoading(true);
              load();
            }}
          >
            Opnieuw proberen
          </Button>
        </div>
      ) : !available ? (
        <div className="rounded-lg border border-amber-300 bg-amber-50/50 p-6 text-sm">
          <p className="flex items-center gap-2 font-medium text-amber-700">
            <InfoIcon size={16} /> De back-upmap is niet beschikbaar
          </p>
          <p className="mt-1 text-muted-foreground">
            Het back-upvolume is op dit moment niet gekoppeld aan de toepassing. De back-ups draaien
            gewoon door op de server; alleen het overzicht kan ze nu niet tonen.
          </p>
        </div>
      ) : (
        <DataTable
          columns={COLUMNS}
          data={backups}
          getRowKey={(b) => b.path}
          initialSort={{ key: "createdAt", dir: "desc" }}
          emptyMessage="Nog geen back-ups gevonden."
        />
      )}
    </AdminShell>
  );
}
